import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function LifeInsuranceRidersIndiana() {
  useEffect(() => {
    document.title = 'Life Insurance Riders in Indiana — Which Add-Ons Are Worth It?';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Life insurance riders in Indiana explained: which add-ons are worth the cost, which to skip, and how to customize your policy for maximum protection.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance riders Indiana, life insurance add-ons Indianapolis, waiver of premium rider Indiana, accelerated death benefit rider');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/life-insurance-riders-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'Life Insurance Riders in Indiana: Which Add-Ons Are Worth It?',
        description: 'A complete guide to life insurance riders available in Indiana — which ones provide real value and which are overpriced extras you can skip.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-01-16T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/life-insurance-riders-indiana`,
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance Riders Indiana', item: `${SITE_URL}/resources/life-insurance-riders-indiana` },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-riders-${i}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => { schemas.forEach((_, i) => document.getElementById(`schema-riders-${i}`)?.remove()); };
  }, []);

  const riders = [
    {
      name: 'Accelerated Death Benefit (ADB)',
      verdict: 'Always Get It',
      verdictColor: 'green',
      cost: 'Usually free',
      icon: 'ri-heart-pulse-line',
      what: 'Allows you to access a portion of your death benefit while still alive if diagnosed with a terminal illness (typically 12-24 months to live).',
      why: 'It\'s free on most Indiana policies and provides critical financial relief during a terminal illness. There\'s no reason not to include it.',
      example: 'An Indianapolis teacher is diagnosed with stage 4 cancer. She accesses $150,000 of her $500,000 death benefit to pay for experimental treatment and home care. Her family still receives $350,000 when she passes.',
    },
    {
      name: 'Waiver of Premium',
      verdict: 'Highly Recommended',
      verdictColor: 'green',
      cost: '$5-$15/month',
      icon: 'ri-shield-check-line',
      what: 'If you become totally disabled and can\'t work, your life insurance premiums are waived — your policy stays active at no cost to you.',
      why: 'Disability is far more common than death during working years. This rider ensures your family stays protected even if you can\'t pay premiums.',
      example: 'A Fishers contractor breaks his back in a fall and can\'t work for 2 years. His $45/month life insurance premiums are waived automatically. His $750,000 policy stays active throughout his recovery.',
    },
    {
      name: 'Child Term Rider',
      verdict: 'Recommended for Parents',
      verdictColor: 'green',
      cost: '$5-$10/month (covers all children)',
      icon: 'ri-parent-line',
      what: 'Adds term life coverage for all your children under one rider. Typically $10,000-$25,000 per child.',
      why: 'Extremely affordable way to cover all your children. Also allows children to convert to permanent coverage as adults without a medical exam.',
      example: 'A Noblesville couple adds a $10/month child rider covering all 3 kids for $20,000 each. When their son turns 25, he converts his $20,000 to a $100,000 whole life policy — no medical exam required.',
    },
    {
      name: 'Guaranteed Insurability Rider',
      verdict: 'Recommended for Young Buyers',
      verdictColor: 'green',
      cost: '$5-$15/month',
      icon: 'ri-lock-line',
      what: 'Allows you to purchase additional life insurance coverage at specific future dates without a medical exam, regardless of your health at that time.',
      why: 'If you\'re young and healthy now but worried about future health issues, this rider locks in your ability to increase coverage later.',
      example: 'A 28-year-old Carmel resident buys a $500K policy with a guaranteed insurability rider. At 35, he\'s been diagnosed with diabetes — but he exercises his rider to add $250K more coverage at standard rates, no exam required.',
    },
    {
      name: 'Return of Premium (ROP)',
      verdict: 'Usually Skip It',
      verdictColor: 'yellow',
      cost: '30-50% more than base premium',
      icon: 'ri-refund-2-line',
      what: 'If you outlive your term policy, all premiums paid are returned to you tax-free.',
      why: 'Sounds great, but the math rarely works out. The extra cost invested in a Roth IRA would typically grow to 2-3x the returned premium amount.',
      example: 'A 35-year-old pays $65/month for a 30-year term with ROP vs. $32/month without. The extra $33/month invested at 8% = $49,000 after 30 years. The ROP refund = $23,400. Investing the difference wins.',
    },
    {
      name: 'Accidental Death Benefit',
      verdict: 'Usually Skip It',
      verdictColor: 'yellow',
      cost: '$5-$20/month',
      icon: 'ri-car-line',
      what: 'Doubles (or triples) your death benefit if you die in an accident.',
      why: 'Most deaths are from illness, not accidents. You\'re better off buying more base coverage than paying for this narrow rider.',
      example: 'Instead of paying $15/month for an accidental death rider, use that $15/month to increase your base coverage by $100,000. Your family is protected regardless of how you die.',
    },
    {
      name: 'Long-Term Care Rider',
      verdict: 'Consider Carefully',
      verdictColor: 'blue',
      cost: '$50-$200/month',
      icon: 'ri-hospital-line',
      what: 'Allows you to access your death benefit to pay for long-term care (nursing home, assisted living, home care) if you can\'t perform 2 of 6 daily living activities.',
      why: 'A good alternative to standalone LTC insurance, which is expensive and hard to qualify for. Best for whole life policies where the death benefit is large enough to fund care.',
      example: 'A 55-year-old Indianapolis executive adds an LTC rider to her $1M whole life policy. At 78, she needs assisted living. She accesses $5,000/month from her death benefit to pay for care — her family receives the remainder when she passes.',
    },
    {
      name: 'Spouse Term Rider',
      verdict: 'Consider Carefully',
      verdictColor: 'blue',
      cost: '$10-$30/month',
      icon: 'ri-user-heart-line',
      what: 'Adds term life coverage for your spouse on your policy.',
      why: 'Convenient, but your spouse may get better rates with their own separate policy. Compare both options before deciding.',
      example: 'A Greenwood couple compares: adding a $250K spouse rider for $25/month vs. a separate $250K policy for the wife at $18/month. The separate policy wins on price and gives the wife her own policy she controls.',
    },
  ];

  return (
    <>
      <title>Life Insurance Riders in Indiana — Which Add-Ons Are Worth It? | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2" />Back to Resources
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <i className="ri-settings-3-line" />Policy Customization
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Life Insurance Riders in Indiana: Which Add-Ons Are Worth It?</h1>
            <p className="text-xl text-white/80 mb-8">Riders can dramatically enhance your coverage — or waste your money. Here's an honest breakdown of every major rider available to Indiana residents.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2" />Updated January 2026</span>
              <span><i className="ri-time-line mr-2" />10 min read</span>
              <span><i className="ri-map-pin-line mr-2" />Indiana Policy Guide</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              A life insurance rider is an optional add-on that modifies or enhances your base policy. Some riders are genuinely valuable — others are expensive extras that benefit the insurance company more than you.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This guide gives you an honest, unbiased assessment of every major rider available to Indiana residents, including which ones to always get, which to skip, and which depend on your situation.
            </p>
          </div>

          {/* Verdict Legend */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h3 className="font-bold text-gray-900 mb-4">Verdict Guide</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { color: 'bg-green-600', label: 'Always Get It', desc: 'High value, low cost — include by default' },
                { color: 'bg-green-500', label: 'Highly Recommended', desc: 'Strong value for most Indiana residents' },
                { color: 'bg-yellow-500', label: 'Usually Skip It', desc: 'Math rarely works in your favor' },
                { color: 'bg-[#1A3A52]', label: 'Consider Carefully', desc: 'Depends on your specific situation' },
              ].map(v => (
                <div key={v.label} className="flex items-start gap-3">
                  <div className={`w-4 h-4 ${v.color} rounded-full mt-1 flex-shrink-0`} />
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{v.label}</p>
                    <p className="text-xs text-gray-600">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Riders */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">8 Life Insurance Riders Explained for Indiana Residents</h2>
            <div className="space-y-6">
              {riders.map((rider, i) => {
                const verdictBg = rider.verdictColor === 'green' ? 'bg-green-600' : rider.verdictColor === 'yellow' ? 'bg-yellow-500' : 'bg-[#1A3A52]';
                const borderColor = rider.verdictColor === 'green' ? 'border-green-200' : rider.verdictColor === 'yellow' ? 'border-yellow-200' : 'border-gray-200';
                return (
                  <div key={rider.name} className={`bg-white border-2 ${borderColor} rounded-2xl p-8`}>
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${rider.icon} text-2xl`} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{rider.name}</h3>
                          <p className="text-sm text-gray-500">Typical cost: <strong>{rider.cost}</strong></p>
                        </div>
                      </div>
                      <span className={`${verdictBg} text-white text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap`}>{rider.verdict}</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">What It Does</p>
                        <p className="text-gray-700 text-sm">{rider.what}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Our Take</p>
                        <p className="text-gray-700 text-sm">{rider.why}</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-5">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Indiana Example</p>
                      <p className="text-gray-700 text-sm italic">{rider.example}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Reference */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Quick Reference: Rider Recommendations by Life Stage</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Rider</th>
                    <th className="px-6 py-4 text-center font-bold">Young Single</th>
                    <th className="px-6 py-4 text-center font-bold">Young Family</th>
                    <th className="px-6 py-4 text-center font-bold">Mid-Career</th>
                    <th className="px-6 py-4 text-center font-bold">Pre-Retirement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { rider: 'Accelerated Death Benefit', young: '✅', family: '✅', mid: '✅', pre: '✅' },
                    { rider: 'Waiver of Premium', young: '✅', family: '✅', mid: '✅', pre: '✅' },
                    { rider: 'Child Term Rider', young: '—', family: '✅', mid: '✅', pre: '—' },
                    { rider: 'Guaranteed Insurability', young: '✅', family: '✅', mid: '—', pre: '—' },
                    { rider: 'Return of Premium', young: '—', family: '—', mid: '—', pre: '—' },
                    { rider: 'Accidental Death Benefit', young: '—', family: '—', mid: '—', pre: '—' },
                    { rider: 'Long-Term Care Rider', young: '—', family: '—', mid: '⚠️', pre: '✅' },
                    { rider: 'Spouse Term Rider', young: '—', family: '⚠️', mid: '⚠️', pre: '—' },
                  ].map(row => (
                    <tr key={row.rider} className="hover:bg-[#F9F7F4]">
                      <td className="px-6 py-4 font-semibold text-gray-900">{row.rider}</td>
                      <td className="px-6 py-4 text-center text-lg">{row.young}</td>
                      <td className="px-6 py-4 text-center text-lg">{row.family}</td>
                      <td className="px-6 py-4 text-center text-lg">{row.mid}</td>
                      <td className="px-6 py-4 text-center text-lg">{row.pre}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-3">✅ Recommended &nbsp;⚠️ Consider based on situation &nbsp;— Skip</p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Build the Right Policy for Your Indiana Family</h2>
            <p className="text-xl text-white/80 mb-8">Benjamin Wright will help you choose the right riders for your situation — and skip the ones that aren't worth the cost.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/instant-quote" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap">Get Instant Quote</Link>
              <Link to="/book-appointment" className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">Talk to an Agent</Link>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { to: '/resources/term-vs-whole-life-indianapolis-homeowners', icon: 'ri-scales-line', title: 'Term vs. Whole Life Insurance', desc: 'Which base policy type is right for Indianapolis homeowners?' },
                { to: '/resources/how-much-life-insurance-indiana-families-need', icon: 'ri-calculator-line', title: 'How Much Coverage Do You Need?', desc: 'Calculate the right coverage amount for your Indiana family' },
                { to: '/resources/life-insurance-mistakes-indiana-families', icon: 'ri-error-warning-line', title: '7 Mistakes Indiana Families Make', desc: 'Avoid the most costly life insurance errors' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${r.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{r.title}</h3>
                  <p className="text-sm text-gray-600">{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Life Insurance Riders?</h2>
            <p className="text-xl text-gray-700 mb-8">Our Indiana-based agents will help you build the right policy with the right riders for your situation.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2" />Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2" />Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
